<template>
  <div class="flex justify-center">
    <div class="w-full md:w-1/2">
      <h3 class="mb-0">
        My Profile
      </h3>
      <p class="text-black text-sm">
        Manage your profile and site settings
      </p>

      <hr>

      <d-message v-if="serverErrorMessage" type="error">
        {{ serverErrorMessage }}
      </d-message>

      <section class="my-10">
        <p class="font-bold mb-5">
          Profile Picture
        </p>
        <div class="flex flex-wrap items-center">
          <div class="w-full md:w-4/12 text-center">
            <d-avatar :text="profile.fullName" :img-src="thumbnailUrl" class="px-4" size="100px" />
            <d-button v-if="form.profilePictureUrl" key="removeimage" flat color="error" class="mt-1" @click="showDeleteImagePrompt()">
              Reset Image
            </d-button>
          </div>
          <div class="w-full md:w-8/12 px-5">
            <input ref="imageUpload" type="file" style="display: none" @change="uploadImage">
            <p class="mb-4">
              Select an image file on your computer (2.5MB max)
            </p>
            <d-button key="browse" :loading="uploadingFile" @click="$refs.imageUpload.click()">
              Choose Image
            </d-button>
          </div>
        </div>
      </section>

      <hr>

      <section class="my-10">
        <form @submit.prevent="handleSubmit">
          <p class="font-bold mb-5">
            My Information
          </p>
          <d-field label="First Name">
            <d-input v-model="form.firstName" required />
          </d-field>
          <d-field label="Last Name">
            <d-input v-model="form.lastName" required />
          </d-field>
          <d-field label="School Phone">
            <d-input v-model="form.schoolPhone" />
          </d-field>
          <d-field label="Personal Phone">
            <d-input v-model="form.altPhone" />
          </d-field>
          <d-field label="Personal Email">
            <d-input v-model="form.personalEmail" type="email" />
          </d-field>
          <d-field label="School Email">
            <d-input v-model="form.schoolEmail" type="email" />
          </d-field>
          <d-field label="Summer Email">
            <d-input v-model="form.summerEmail" type="email" />
          </d-field>
          <d-field label="Contact Preference">
            <d-select v-model="form.contactPreference">
              <option value="School Email">
                School Email
              </option>
              <option value="Summer Email">
                Summer Email
              </option>
              <option value="Personal Email">
                Personal Email
              </option>
            </d-select>
          </d-field>
          <d-field label="Short Biography">
            <d-input v-model="form.bio" type="textarea" />
          </d-field>
          <div class="flex justify-end">
            <d-btn type="submit" class="mt-3" :loading="savingChanges">
              Save Changes
            </d-btn>
          </div>
        </form>
      </section>

      <hr>

      <section class="my-10">
        <div class="flex items-center">
          <div class="w-7/12">
            <p class="font-bold">
              Password
            </p>
            <p class="text-black text-sm mb-5 ">
              Make your password stronger, or change it to continue to keep your account secure.
            </p>
          </div>
          <div class="w-5/12 text-right">
            <d-btn href="/ManageAccount/ChangePassword">
              Change My Password
            </d-btn>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
  import axios from 'axios';
  import { mapGetters } from 'vuex';

  export default {
    parent: true,
    data() {
      return {
        uploadingFile: false,
        savingChanges: false,
        serverErrorMessage: '',
        form: {
          firstName: '',
          lastName: '',
          profileImageUrl: '',
          bio: '',
          bestTimeToContact: '',
          contactPreference: '',
          personalEmail: '',
          summerEmail: '',
          schoolEmail: '',
          schoolPhone: '',
          altPhone: '',
        },
      };
    },

    computed: {
      ...mapGetters('d2Auth', ['profile', 'hasRole']),

      thumbnailUrl() {
        return this.form.profileImageUrl ? `${this.form.profileImageUrl}?${new Date().getTime()}` : '';
      },
    },
    mounted() {
      this.getUserProfile();
    },
    methods: {
      async getUserProfile() {
        const { data } = await axios.get(`/api/public-profile/${this.profile.id}`);
        this.form.firstName = data.firstName;
        this.form.lastName = data.lastName;
        this.form.bio = data.bio;
        this.form.profileImageUrl = data.profileImageUrl;
        this.form.bestTimeToContact = data.bestTimeToContact;
        this.form.contactPreference = data.contactPreference;
        this.form.summerEmail = data.summerEmail;
        this.form.schoolEmail = data.schoolEmail;
        this.form.personalEmail = data.personalEmail;
        this.form.schoolPhone = data.schoolPhone;
        this.form.altPhone = data.altPhone;
      },
      async handleSubmit() {
        this.savingChanges = true;
        const { data } = await axios.patch(`/api/public-profile/${this.profile.id}`, this.form);
        this.form = data;
        this.$toast.success('Profile has been updated');
        this.savingChanges = false;
      },

      async uploadImage() {
        this.uploadingFile = true;
        const file = this.$refs.imageUpload.files[0];
        const form = new FormData();
        form.append('File', file);
        try {
          const { data } = await axios.put(`/api/public-profile/${this.profile.id}/image`, form, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          });
          this.form.profilePictureUrl = data;
          await this.getUserProfile();
          this.$toast.open({
            message: 'Image was successfully uploaded',
            color: 'success',
          });
        } catch (error) {
          this.$toast.open({
            duration: 5000,
            message: error.response.data[0],
            color: 'error',
          });
        } finally {
          this.resetFileInput();
          this.uploadingFile = false;
        }
      },

      resetFileInput() {
        this.$refs.imageUpload.type = 'text';
        this.$refs.imageUpload.type = 'file';
        this.$refs.imageUpload.value = '';
      },
    },
  };
</script>